import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía del módulo de Radicación de correspondencia oficial.
 *
 * Cubre la correspondencia INSTITUCIONAL — oficios entre entidades,
 * circulares, memorandos internos — regulada por la Ley 594/2000
 * (Archivo General de la Nación) y el Acuerdo AGN 060/2001.
 *
 * Este módulo NO maneja PQRSDF (derecho de petición del ciudadano).
 * Para eso ver /docs/pqrsdf — son piezas independientes con su propio
 * consecutivo, su propio marco legal y su propio flujo.
 *
 * Audiencia: archivo central + ventanilla única + admin_tenant.
 */
export function DocsRadicacionPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Radicación de
          correspondencia
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Radicación de correspondencia oficial
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 20-25 min. Para configurar tipos
          documentales, dependencias archivo, TRD y firmas por primera vez en
          un tenant cuenta 2-4 horas adicionales con el archivo central de la
          alcaldía.
        </div>

        <div className="callout tip">
          ¿Buscas <strong>PQRSDF</strong> (peticiones del ciudadano regidas por
          Ley 1755/2015)? Es otro módulo independiente →{" "}
          <Link to="/docs/pqrsdf">Ver guía PQRSDF</Link>.
        </div>

        {/* ---- 1. Qué es ---- */}
        <Section title="1. Qué es Radicación de correspondencia">
          <p>
            Radicar correspondencia es asignar un{" "}
            <strong>número único, consecutivo y por año</strong> a un documento
            oficial que entra, sale o circula internamente entre dependencias
            de la alcaldía, dejando trazabilidad archivística completa.
          </p>
          <p>
            Cubre exclusivamente <strong>comunicaciones institucionales</strong>:
            oficios entre entidades (otra alcaldía, gobernación, ministerio,
            contraloría, juzgado, particular que escribe vía oficio formal),
            circulares, memorandos internos. Lo que escribe el{" "}
            <strong>ciudadano persona-natural</strong> ejerciendo derecho de
            petición no es correspondencia: es PQRSDF y va por otro módulo.
          </p>
          <div className="callout">
            <strong>Regla práctica:</strong> si el documento entrante es un{" "}
            <em>oficio firmado con número de radicado externo</em> de otra
            entidad → correspondencia. Si es un mensaje de WhatsApp o
            formulario web de un ciudadano → PQRSDF.
          </div>
        </Section>

        {/* ---- 2. Numeración ---- */}
        <Section title="2. Numeración del radicado">
          <p>
            Cada radicado de correspondencia se identifica con un consecutivo
            legible que viaja con el documento en notificaciones, acuses y
            stickers físicos. Generado por{" "}
            <code>
              mipueblo.domain.correspondencia.build_numero_completo()
            </code>{" "}
            con <code>pg_advisory_xact_lock</code> para evitar duplicados bajo
            concurrencia.
          </p>
          <pre className="code-block">{`{SLUG3}-{PREFIX}-{YYYY}-{NNNNN}

PREFIX = CORR (entrada) | OFI (salida) | MEM (interna)

Ejemplos:
  CAJ-CORR-2026-00001   ← oficio entrante a Cajicá
  CAJ-OFI-2026-00042    ← oficio que envía Cajicá
  CAJ-MEM-2026-00007    ← memorando interno`}</pre>
          <Ul
            items={[
              <>
                <code>SLUG3</code> — slug del tenant en mayúsculas, 3 letras.
              </>,
              <>
                <code>PREFIX</code> — tipo de correspondencia (CORR / OFI /
                MEM).
              </>,
              <>
                <code>YYYY</code> — año en que se radicó (reinicia cada 1 de
                enero).
              </>,
              <>
                <code>NNNNN</code> — consecutivo por (tenant, año, tipo) con
                relleno a 5 dígitos.
              </>,
            ]}
          />
          <div className="callout">
            Los 3 consecutivos (entrada / salida / interna) son{" "}
            <strong>independientes entre sí</strong>. Y son también
            independientes del consecutivo de{" "}
            <Link to="/docs/pqrsdf">PQRSDF</Link> (
            <code>SLUG-YYYY-NNNNN</code> sin prefijo).
          </div>
        </Section>

        {/* ---- 3. Marco legal ---- */}
        <Section title="3. Marco legal archivístico">
          <Table
            head={["Norma", "Qué regula", "Impacto en MiPueblo"]}
            rows={[
              [
                "Ley 594 de 2000",
                "Ley General de Archivos",
                "Obliga conservación, foliación, retención y transferencia documental.",
              ],
              [
                "Acuerdo AGN 060 de 2001",
                "Pautas de correspondencia y archivo",
                "Define la radicación oficial, el sello/estampa, el acuse y el libro radicador.",
              ],
              [
                "Decreto 2609 de 2012",
                "Gestión documental",
                "Establece los 8 procesos archivísticos y obligaciones de las TRD.",
              ],
              [
                "Acuerdo AGN 002 de 2014",
                "Gestión de documentos electrónicos",
                "Reconoce validez del radicado y el sello digital.",
              ],
              [
                "NTC 4436",
                "Conservación de documentos",
                "Pautas técnicas para los escaneos (resolución, formato, color).",
              ],
              [
                "Ley 527 de 1999",
                "Comercio electrónico y firma digital",
                "Soporte legal de la firma digital PAdES para oficios de salida.",
              ],
            ]}
          />
        </Section>

        {/* ---- 4. Entrante ---- */}
        <Section title="4. Correspondencia entrante (CORR)">
          <p>
            Oficio en papel o medio formal (email institucional, mensajería)
            que llega a la <strong>ventanilla única</strong>. Tipo{" "}
            <code>entrada</code>, prefijo <code>CORR</code>. Requiere rol{" "}
            <code>admin_tenant</code>, <code>supervisor</code> o{" "}
            <code>radicador</code> (separación de deberes: la ventanilla
            recibe, la dependencia destino atiende).
          </p>
          <Ol>
            <li>
              Gestión Documental → <Kbd>Correspondencia</Kbd> →{" "}
              <Kbd>Nueva entrada</Kbd>.
            </li>
            <li>
              Seleccionar <strong>tipo documental</strong> (oficio, tutela,
              circular, requerimiento de organismo de control). Hereda
              prioridad, SLA y reglas de confidencialidad del{" "}
              <code>TipoDocumental</code>.
            </li>
            <li>
              Llenar remitente — <strong>nombre obligatorio</strong> para
              entrada (
              <code>remitente_nombre</code> mínimo). También: NIT/CC, email,
              teléfono, dirección.
            </li>
            <li>
              Asunto, contenido (resumen para indexar), medio (
              <code>fisico</code> / <code>email</code> / <code>whatsapp</code>{" "}
              / <code>web</code> / <code>mensajeria</code>).
            </li>
            <li>
              Asignar <strong>dependencia destino</strong>.
            </li>
            <li>
              <Kbd>Crear</Kbd> → se genera <code>CAJ-CORR-2026-NNNNN</code>{" "}
              vía <code>next_numero_corr</code>.
            </li>
            <li>
              Subir el <strong>escaneo</strong> del documento (PDF / JPG / PNG
              / WebP, máx 25 MB). MiPueblo lo estampa automáticamente con
              número, fecha y nombre de quien recibió (
              <code>estampar_correspondencia</code>) y deja disponibles:
              <Ul
                items={[
                  <>
                    <code>/scan?version=original</code> — tal cual lo
                    entregaron
                  </>,
                  <>
                    <code>/scan?version=estampado</code> — con el sello digital
                  </>,
                  <>
                    <code>/acuse.pdf</code> — A5 para entregar al portador (
                    <code>render_acuse_pdf_bytes</code>)
                  </>,
                  <>
                    <code>/sticker.pdf</code> — 80×40 mm para impresora
                    térmica, se pega al físico (
                    <code>render_sticker_pdf_bytes</code>)
                  </>,
                ]}
              />
            </li>
            <li>
              Si hay <code>remitente_email</code> o{" "}
              <code>remitente_telefono</code>, se encola{" "}
              <strong>acuse automático</strong> con el PDF estampado adjunto.
            </li>
          </Ol>
          <div className="callout warn">
            El módulo solo responde si{" "}
            <code>correspondencia_habilitada(tenant)</code> = true. Si no, el
            backend devuelve 403. Habilitar desde panel super-admin del
            tenant.
          </div>
        </Section>

        {/* ---- 5. Saliente ---- */}
        <Section title="5. Correspondencia saliente (OFI)">
          <p>
            Oficio que la alcaldía <strong>emite</strong> hacia otra entidad o
            particular formal. Tipo <code>salida</code>, prefijo{" "}
            <code>OFI</code>.
          </p>
          <Ol>
            <li>
              Gestión Documental → <Kbd>Correspondencia</Kbd> →{" "}
              <Kbd>Nueva salida</Kbd>.
            </li>
            <li>
              Elegir <strong>tipo documental</strong>. Si{" "}
              <code>requiere_firma_salida = true</code>, MiPueblo{" "}
              <strong>bloquea el despacho</strong> hasta que se firme.
            </li>
            <li>
              Llenar destinatario: dependencia externa, email (
              <code>destinatario_email</code>) u oficio dirigido a nombre libre
              (<code>destinatario_externo</code>).
            </li>
            <li>
              Redactar el contenido. Si responde a una entrada previa, vincular
              con <code>responde_a_id</code> para mantener trazabilidad
              cruzada entre el oficio recibido y la respuesta.
            </li>
            <li>
              Se genera <code>CAJ-OFI-2026-NNNNN</code>.
            </li>
            <li>
              <strong>Firma del jerárquico</strong> — el alcalde, secretario o
              jefe de dependencia firma el PDF generado (firma digital PAdES,
              Ley 527/1999). Ver{" "}
              <Link to="/tenants">configuración de firma</Link>.
            </li>
            <li>
              <strong>Despachar</strong>: email institucional, mensajería
              certificada (4-72 / Servientrega) o entrega física. Marcar como{" "}
              <code>respondido</code> y luego <code>archivado</code>. La copia
              firmada queda en el expediente para archivo central.
            </li>
          </Ol>
        </Section>

        {/* ---- 6. Interna ---- */}
        <Section title="6. Memorando interno (MEM)">
          <p>
            Comunicación entre dependencias de la <strong>misma alcaldía</strong>{" "}
            (Hacienda → Planeación, por ejemplo). Tipo <code>interna</code>,
            prefijo <code>MEM</code>.
          </p>
          <Ul
            items={[
              <>
                No requiere remitente externo — origen y destino son
                dependencias internas.
              </>,
              <>
                Útil para solicitudes formales internas, traslados de
                expedientes, comunicaciones de directivas.
              </>,
              <>
                La firma electrónica simple del jefe de dependencia es
                suficiente (no exige PAdES).
              </>,
              <>
                Quedan archivados por dependencia origen y dependencia destino
                (doble copia).
              </>,
            ]}
          />
        </Section>

        {/* ---- 7. Anexos y foliación ---- */}
        <Section title="7. Anexos y foliación">
          <p>
            Cada radicado de correspondencia puede llevar{" "}
            <strong>anexos numerados</strong> (carta + 3 anexos = 4 piezas
            documentales). MiPueblo asigna foliación correlativa al expediente
            digital:
          </p>
          <Ul
            items={[
              <>
                <strong>Folio principal</strong> — el oficio en sí.
              </>,
              <>
                <strong>Folios de anexos</strong> — numerados 1..N en el orden
                en que se cargaron, con descripción.
              </>,
              <>
                Los escaneos se conservan en <strong>PDF/A</strong> para
                garantizar lectura a largo plazo (NTC 4436, requisito AGN).
              </>,
              <>
                Resolución mínima recomendada: <strong>300 dpi color</strong>{" "}
                para documentos con sellos, <strong>200 dpi B/N</strong> para
                texto plano.
              </>,
            ]}
          />
        </Section>

        {/* ---- 8. Devolución / no recibido ---- */}
        <Section title="8. Devolución y no recibido">
          <p>
            Si un oficio entrante <strong>no corresponde</strong> a la alcaldía
            (mal dirigido, destinatario inexistente, fuera de competencia),
            ventanilla puede devolverlo sin radicar — pero si ya se radicó por
            error, se anula (ver §10).
          </p>
          <p>
            Para correspondencia saliente, si la mensajería certificada reporta{" "}
            <strong>no entregado</strong> (mudanza, rechazo), se registra el
            evento en el expediente con la guía de devolución y se reintenta
            por otro canal. El oficio mantiene su número original.
          </p>
        </Section>

        {/* ---- 9. Trazabilidad ---- */}
        <Section title="9. Consulta y trazabilidad">
          <p>
            Cada radicado tiene una <strong>cadena de eventos firmados</strong>{" "}
            (modelo <code>RadicadoEvento</code>) verificable con{" "}
            <code>verify_chain</code>. Quién hizo qué, cuándo, desde qué IP —
            es inmutable y firma hash-encadenada estilo blockchain ligera.
          </p>
          <Ul
            items={[
              <>
                <strong>Consulta interna</strong>: la dependencia destino y
                archivo central ven el detalle completo según permisos (
                <code>puede_ver_contenido</code> aplica reservas de
                confidencialidad).
              </>,
              <>
                <strong>Consulta externa del remitente</strong>: si dio email o
                teléfono, recibe el acuse con el número y puede consultar
                estado vía portal.
              </>,
              <>
                <strong>Confidencialidad</strong>: documentos marcados como{" "}
                <code>reservado</code> ocultan contenido y datos del remitente
                fuera de la dependencia autorizada.
              </>,
            ]}
          />
        </Section>

        {/* ---- 10. Anulación ---- */}
        <Section title="10. Anulación de radicado">
          <p>
            Un radicado <strong>nunca se borra</strong> (Ley 594/2000 — los
            documentos públicos son inmodificables). Si fue creado por error se
            marca como <code>anulado</code>:
          </p>
          <Ul
            items={[
              <>
                Solo <code>admin_tenant</code> o <code>supervisor</code> puede
                anular.
              </>,
              <>
                Requiere <strong>justificación escrita</strong> obligatoria
                (queda en la auditoría inmutable junto con actor, fecha, IP).
              </>,
              <>
                El consecutivo <strong>no se reusa</strong> — el hueco queda
                visible en el libro radicador con la nota de anulación.
              </>,
              <>
                Los escaneos y eventos previos se conservan para auditoría —
                solo se marca el estado, no se eliminan datos.
              </>,
            ]}
          />
        </Section>

        {/* ---- 11. Transferencia al archivo central ---- */}
        <Section title="11. Transferencia primaria al archivo central">
          <p>
            Al cierre de cada vigencia (típicamente fin del periodo gestor,
            1-2 años) los expedientes activos se{" "}
            <strong>transfieren al archivo central</strong> según las Tablas de
            Retención Documental (TRD) del tenant:
          </p>
          <Ol>
            <li>
              Generar <strong>inventario único documental</strong> (formato
              AGN) desde <code>/reportes</code>.
            </li>
            <li>
              Validar foliación y completitud por expediente.
            </li>
            <li>
              Cambiar dependencia responsable a <code>archivo_central</code>{" "}
              (los expedientes quedan en estado <code>archivado</code>, solo
              lectura).
            </li>
            <li>
              Generar acta de transferencia firmada digitalmente por el
              gestor saliente y el archivista receptor.
            </li>
          </Ol>
        </Section>

        {/* ---- 12. TRD ---- */}
        <Section title="12. Tablas de Retención Documental (TRD)">
          <p>
            Las TRD definen <strong>cuánto tiempo</strong> debe conservarse
            cada tipo documental antes de transferir al archivo histórico o
            eliminar (con acta). Cada <code>TipoDocumental</code> en MiPueblo
            se vincula a una serie/subserie de la TRD del tenant.
          </p>
          <p>
            La configuración detallada (series, subseries, tiempos de gestión,
            tiempos de archivo central, disposición final) se documenta
            aparte → <Link to="/docs/gestion-documental">Ver guía de
            Gestión Documental</Link>.
          </p>
        </Section>

        {/* ---- 13. Reportes ---- */}
        <Section title="13. Reportes archivísticos">
          <p>Disponibles desde <code>/reportes</code> del tenant:</p>
          <Ul
            items={[
              <>
                <strong>Libro radicador de correspondencia</strong> — formato
                AGN 060/2001, separable por entrada / salida / interna y por
                periodo.
              </>,
              <>
                <strong>Consecutivo anual</strong> con huecos justificados
                (anulaciones) — útil para inventarios documentales.
              </>,
              <>
                <strong>Inventario único documental</strong> formato AGN para
                transferencias.
              </>,
              <>
                <strong>Anulaciones</strong> con justificación, actor y fecha,
                por periodo.
              </>,
              <>
                <strong>Pendientes de archivo</strong> — expedientes cerrados
                aún no transferidos al archivo central.
              </>,
              <>
                Exportable a CSV / XLSX / PDF firmado.
              </>,
            ]}
          />
        </Section>

        {/* ---- 14. Backup y retención ---- */}
        <Section title="14. Backup legal y retención mínima">
          <p>
            La Ley 594/2000 obliga la conservación de los documentos públicos
            por los tiempos que definan las TRD del tenant. MiPueblo garantiza:
          </p>
          <Ul
            items={[
              <>
                <strong>Backup diario</strong> de la base de datos y los
                escaneos (S3 + réplica geográfica).
              </>,
              <>
                Escaneos en <strong>PDF/A</strong> para lectura a 50+ años.
              </>,
              <>
                Eventos en <strong>cadena hash-firmada</strong> — alteraciones
                detectables vía <code>verify_chain</code>.
              </>,
              <>
                Exportación completa del archivo del tenant en caso de
                terminación del servicio (formato auditable).
              </>,
              <>
                Retención mínima recomendada: <strong>10 años</strong> en
                archivo central, según TRD por tipo documental.
              </>,
            ]}
          />
        </Section>

        {/* ---- 15. Buenas prácticas ---- */}
        <Section title="15. Buenas prácticas">
          <Checklist
            items={[
              <>
                <strong>Capacitar a ventanilla</strong> en la distinción
                correspondencia vs PQRSDF. Si quien escribe es un{" "}
                <em>ciudadano persona-natural</em> ejerciendo derecho de
                petición → es PQRSDF, va por{" "}
                <Link to="/docs/pqrsdf">otro módulo</Link>.
              </>,
              <>
                <strong>Configurar tipos documentales</strong> antes de
                producción, con prioridad, SLA y reglas de confidencialidad
                por defecto. Reduce errores de captura.
              </>,
              <>
                <strong>Imprimir sticker térmico</strong> y pegarlo al
                documento físico antes de mandarlo a la dependencia. Sin
                sticker el documento se pierde en circulación interna.
              </>,
              <>
                <strong>Estampa digital obligatoria</strong> en escaneos — no
                aceptar como evidencia el escaneo original sin sello, en
                ningún reporte ni acuse.
              </>,
              <>
                <strong>Vincular respuestas</strong> de OFI a CORR con{" "}
                <code>responde_a_id</code> para mantener trazabilidad del hilo
                completo entre entidades.
              </>,
              <>
                <strong>No anular para &quot;limpiar&quot;</strong> — archivar.
                La anulación es solo para errores reales de captura.
              </>,
              <>
                Generar el <strong>libro radicador</strong> mensualmente y
                archivarlo firmado — es exigible en auditorías de contraloría.
              </>,
              <>
                Revisar trimestralmente expedientes <strong>pendientes de
                transferencia</strong> al archivo central según TRD.
              </>,
            ]}
          />
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13.5,
        }}
      >
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{
                borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)",
              }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ExtLink exportado para coherencia con otros docs aunque no se use aquí.
void ExtLink;
